import React from 'react';
import {
  Eye,
  Check,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

interface BadgeLivePreviewProps {
  book: Book;
  badgeStyle: 'pill' | 'card' | 'shield' | 'button';
  theme: 'emerald' | 'light' | 'dark' | 'indigo';
  previewContext: 'isolated' | 'mockup';
  verifyUrl: string;
  onPreviewContextChange: (ctx: 'isolated' | 'mockup') => void;
  onOpenVerifyModal?: (uibn: string) => void;
}

export const BadgeLivePreview: React.FC<BadgeLivePreviewProps> = ({
  book,
  badgeStyle,
  theme,
  previewContext,
  verifyUrl,
  onPreviewContextChange,
  onOpenVerifyModal,
}) => {
  const cleanUibn = book.uibn;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-2xs space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Eye className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
            Pratinjau Langsung (Live Preview)
          </span>
          <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded shrink-0">
            Klik untuk uji coba verifikasi
          </span>
        </div>

        {/* View context toggle */}
        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg text-xs font-medium self-start sm:self-auto">
          <button
            type="button"
            onClick={() => onPreviewContextChange('isolated')}
            className={`px-2 py-0.5 rounded text-[11px] transition ${
              previewContext === 'isolated'
                ? 'bg-white text-slate-900 shadow-2xs font-semibold'
                : 'text-slate-500'
            }`}
          >
            Lencana Saja
          </button>
          <button
            type="button"
            onClick={() => onPreviewContextChange('mockup')}
            className={`px-2 py-0.5 rounded text-[11px] transition ${
              previewContext === 'mockup'
                ? 'bg-white text-slate-900 shadow-2xs font-semibold'
                : 'text-slate-500'
            }`}
          >
            Simulasi Situs Penerbit
          </button>
        </div>
      </div>

      {/* Preview Canvas */}
      {previewContext === 'isolated' ? (
        <div className="p-6 bg-slate-100/70 border border-slate-200/80 rounded-xl flex items-center justify-center min-h-[100px]">
          {/* Real Clickable Interactive Badge Preview */}
          <a
            href={verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (onOpenVerifyModal) {
                e.preventDefault();
                onOpenVerifyModal(book.uibn);
              }
            }}
            className="transition transform hover:scale-[1.02] cursor-pointer inline-block"
            title="Klik untuk membuka status verifikasi buku"
          >
            {badgeStyle === 'pill' && (
              <div
                className={`inline-flex items-center gap-2.5 px-4 py-2 rounded-full border shadow-xs transition select-none ${
                  theme === 'emerald'
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : theme === 'light'
                    ? 'bg-white text-emerald-900 border-emerald-300'
                    : theme === 'dark'
                    ? 'bg-slate-900 text-white border-slate-700'
                    : 'bg-indigo-700 text-white border-indigo-800'
                }`}
              >
                <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-2xs shrink-0">
                  <Check className="w-3.5 h-3.5 stroke-[3]" />
                </div>
                <span className="text-xs font-bold tracking-tight">
                  Buku ini terdaftar resmi di UIBN
                </span>
                <span
                  className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                    theme === 'light'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-black/20 text-white/90'
                  }`}
                >
                  {cleanUibn}
                </span>
              </div>
            )}

            {badgeStyle === 'shield' && (
              <div className="inline-flex rounded-lg overflow-hidden border border-slate-300 shadow-2xs font-sans text-xs font-bold">
                <span className="bg-slate-900 text-indigo-200 px-3 py-1.5 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" /> UIBN
                </span>
                <span className="bg-emerald-600 text-white px-3 py-1.5 flex items-center gap-1">
                  <Check className="w-3 h-3 stroke-[3]" /> Terdaftar Resmi
                </span>
              </div>
            )}

            {badgeStyle === 'card' && (
              <div
                className={`w-full max-w-[320px] p-3.5 rounded-xl border shadow-xs transition ${
                  theme === 'dark'
                    ? 'bg-slate-900 text-white border-slate-700'
                    : theme === 'emerald'
                    ? 'bg-emerald-800 text-white border-emerald-900'
                    : theme === 'indigo'
                    ? 'bg-indigo-900 text-white border-indigo-950'
                    : 'bg-white text-slate-900 border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <Check className="w-3 h-3 stroke-[3]" /> Terdaftar Resmi UIBN
                  </span>
                  <span className="font-mono text-[10px] font-semibold text-slate-400">
                    {cleanUibn}
                  </span>
                </div>
                <div className="font-bold text-xs truncate mb-0.5">{book.title}</div>
                <div className="text-[11px] text-slate-400 truncate mb-2">
                  Penerbit: {book.publisherName} • {book.publicationYear}
                </div>
                <div className="flex items-center justify-between text-[11px] font-semibold text-indigo-400 pt-2 border-t border-slate-200/20">
                  <span>Status: Sah & Disetujui</span>
                  <span className="flex items-center gap-1 hover:underline">
                    Periksa Keaslian <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </div>
            )}

            {badgeStyle === 'button' && (
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs transition">
                <ShieldCheck className="w-4 h-4 text-emerald-300" />
                <span>Verifikasi di UIBN Nasional</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
              </div>
            )}
          </a>
        </div>
      ) : (
        /* Simulated Publisher Web Store Page */
        <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-xs">
          {/* Browser bar */}
          <div className="bg-slate-100 px-3 py-1.5 border-b border-slate-200 flex items-center gap-2 text-[11px] text-slate-500">
            <div className="flex gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block"></span>
            </div>
            <span className="bg-white px-2.5 py-0.5 rounded border border-slate-200 font-mono text-[10px] text-slate-600 flex-1 truncate">
              https://penerbit-{book.publisherName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com/buku/{cleanUibn.toLowerCase()}
            </span>
          </div>

          {/* Simulated Content */}
          <div className="p-4 flex gap-4 items-start">
            <div className="w-16 h-22 bg-slate-200 rounded-lg flex items-center justify-center text-slate-400 shrink-0 font-bold text-xs border border-slate-300">
              Sampul
            </div>
            <div className="flex-1 min-w-0 space-y-1.5">
              <h5 className="font-bold text-sm text-slate-900 truncate">{book.title}</h5>
              <p className="text-xs text-slate-500">
                Oleh <strong>{book.authorName}</strong> | {book.publisherName} ({book.publicationYear})
              </p>
              <div className="text-xs font-semibold text-emerald-700">Rp 95.000 (Tersedia)</div>

              {/* The Embedded Badge on the Mockup */}
              <div className="pt-2">
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (onOpenVerifyModal) {
                      e.preventDefault();
                      onOpenVerifyModal(book.uibn);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 text-[11px] font-bold shadow-2xs hover:bg-emerald-100 transition cursor-pointer"
                >
                  <Check className="w-3 h-3 text-emerald-600 stroke-[3]" />
                  <span>Buku ini terdaftar resmi di UIBN</span>
                  <span className="font-mono text-[10px] text-emerald-700 opacity-90">({cleanUibn})</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
